import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { HiOutlineIdentification } from "react-icons/hi";

// Qué se le exige a un socio para entrar en ESTE club.
//
// Por defecto TODO es opcional salvo nombre y apellidos (decidido en FE #397:
// muchos socios llegan sin documento a mano). Estos interruptores aprietan POR
// CLUB y el backend valida además del formulario (422 con código estable), así
// que encender esto bloquea de verdad, no solo pinta asteriscos.
//
//   · Foto y escaneo actúan en el formulario público del QR — petición de Kong
//     House (02-09-2026): la mayoría de socios nuevos se los saltaba.
//   · El acta firmada actúa AL DAR DE ALTA (tablet y aprobación de preinscripción),
//     NO en el QR: la solicitud entra sin firma y se firma al aprobar — petición de
//     CRUO (11-09-2026). Hasta entonces la tablet solo avisaba y dejaba seguir.
//
// Mismo criterio que ActaPlantillaCard para vivir fuera de Settings.jsx: decide
// qué se le exige a un socio para entrar, y merece poder probarse sola.
// Un dato, un render: las tres claves del venue → los tres interruptores, en un sitio.
const leerFlags = (venue) => ({
  foto: Boolean(venue?.prereg_photo_required),
  escaneo: Boolean(venue?.prereg_dni_required),
  firma: Boolean(venue?.prereg_signature_required),
});

export default function PreinscripcionFormCard({ token, venueID, puedeGestionar, onError }) {
  // null = sin cargar (o falló la carga): no se pintan interruptores que no se
  // sabe si dicen la verdad — un toggle "apagado" que en realidad es desconocido
  // le hace creer al club que no exige foto cuando quizá sí.
  const [flags, setFlags] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState("");

  const cargar = useCallback(async () => {
    if (!token || !venueID) return;
    try {
      const res = await axios.get(`/v1/venues/${venueID}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFlags(leerFlags(res.data));
      setAviso("");
    } catch {
      setFlags(null);
      setAviso("No se pudo cargar la configuración del formulario.");
    }
  }, [token, venueID]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const cambiar = async (campo, valor) => {
    // Nada de optimismo: un interruptor de exigencia que "parece" encendido y no
    // lo está engaña al club. Primero el PATCH; el estado, de la respuesta.
    try {
      setGuardando(true);
      setAviso("");
      const res = await axios.patch(
        `/v1/venues/${venueID}/prereg-required-fields`,
        { [campo]: valor },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setFlags(leerFlags(res.data));
    } catch (err) {
      const detalle = err?.response?.data?.detail || "No se pudo guardar el cambio.";
      setAviso(detalle);
      onError?.(detalle);
    } finally {
      setGuardando(false);
    }
  };

  const fila = (titulo, descripcion, campo, valor) => (
    <label className="flex items-start justify-between gap-4 py-3">
      <span>
        <span className="block text-sm font-medium text-gray-900">{titulo}</span>
        <span className="block text-sm text-gray-500">{descripcion}</span>
      </span>
      <input
        type="checkbox"
        className="mt-1 h-5 w-5 accent-purple-600"
        checked={valor}
        disabled={!puedeGestionar || guardando || flags === null}
        onChange={(e) => cambiar(campo, e.target.checked)}
      />
    </label>
  );

  return (
    <section className="mb-10">
      <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-4">
        <HiOutlineIdentification className="text-purple-600" />
        Requisitos para dar de alta
      </h2>
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <p className="text-sm text-gray-700">
          Por defecto solo nombre y apellidos son obligatorios. Estos interruptores
          aprietan SOLO para este club, y el bloqueo lo aplica también el servidor —
          no es solo visual.
        </p>
        <div className="mt-2 divide-y divide-gray-100">
          {fila(
            "Exigir foto del socio",
            "Sin selfie o foto subida, la preinscripción no se puede enviar.",
            "prereg_photo_required",
            Boolean(flags?.foto),
          )}
          {fila(
            "Exigir escaneo del documento",
            "Hace falta al menos el anverso (los pasaportes no tienen reverso).",
            "prereg_dni_required",
            Boolean(flags?.escaneo),
          )}
          {fila(
            "Exigir acta firmada",
            "Al dar de alta (desde la tablet o al aprobar una preinscripción) hace falta la firma del socio. La solicitud del QR se puede enviar sin firmar.",
            "prereg_signature_required",
            Boolean(flags?.firma),
          )}
        </div>
        {aviso && <p className="mt-2 text-sm text-red-600">{aviso}</p>}
        {!puedeGestionar && (
          <p className="mt-2 text-sm text-gray-500">Solo el propietario puede cambiar esto.</p>
        )}
      </div>
    </section>
  );
}
